import { useEffect, useRef } from 'react';

import { HorizontalContainer, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import AdvisoryCountChips from 'components/airspace/AdvisoryCountChips';
import AirspaceCardList from 'components/airspace/AirspaceCardList';
import { getVisibleRestrictions } from 'components/airspace/AirspaceCardList/visibility';
import { findWeatherMatchKey } from 'components/airspace/AirspaceCardList/weatherMatching';
import FooterNote from 'components/airspace/FooterNote';
import NewOperationButton from 'components/airspace/NewOperationButton';
import SheetHeader from 'components/airspace/SheetHeader';
import WeatherStrip from 'components/airspace/WeatherStrip';

import useMobileViewport from 'hooks/useMobileViewport';

import useEscapeToDismiss from './hooks/useEscapeToDismiss';
import useSheetDrag from './hooks/useSheetDrag';
import useSheetSnap from './hooks/useSheetSnap';

import { TEST_ID } from './constants';
import type {
    AirspaceRestriction,
    AirspaceSummary,
    FunctionComponent,
    Props,
    SeverityCounts
} from './types';

import styles from './styles.module.scss';

const STATE_CLASS = {
    full: styles.full,
    hidden: styles.hidden,
    peek: styles.peek,
    prompt: styles.prompt
};

const countBySeverity = (restrictions: AirspaceRestriction[]): SeverityCounts => {
    const counts: SeverityCounts = {};

    for (const item of restrictions) {
        counts[item.severity] = (counts[item.severity] ?? 0) + 1;
    }

    return counts;
};

const AirspaceSheet: FunctionComponent<Props> = ({
    addressOverride = null,
    className = '',
    data,
    isLoading = false,
    onClose,
    regionOverride = null,
    testId = TEST_ID,
    weather = null
}) => {
    const { collapse, dismiss, expand, showContent, state } = useSheetSnap();
    const { isMobile } = useMobileViewport();
    const lastDataRef = useRef<AirspaceSummary | null>(null);
    const sheetRef = useRef<HTMLDivElement | null>(null);
    const dragHandleRef = useRef<HTMLDivElement | null>(null);

    useSheetDrag({
        dragHandleRef,
        isEnabled: isMobile && state !== 'prompt' && state !== 'hidden',
        isExpanded: state === 'full',
        onCollapse: collapse,
        onDismiss: dismiss,
        onExpand: expand,
        sheetRef
    });

    useEscapeToDismiss({ onDismiss: dismiss });

    useEffect(() => {
        if (data !== null && data !== lastDataRef.current) {
            lastDataRef.current = data;
            showContent();
        }
    }, [data, showContent]);

    const stateClass = STATE_CLASS[state];
    const isPrompt = state === 'prompt' || data === null;

    if (isPrompt) {
        return (
            <VerticalContainer
                className={`${styles.sheet} ${stateClass} ${className}`}
                testId={testId}
            />
        );
    }

    const { address: apiAddress, point, summary } = data;
    const address = addressOverride ?? apiAddress;
    const region = regionOverride;
    // Chips and advisory label count only what's actually rendered in the list — the
    // unbounded count (backend returns 15 nm worth of airports) doesn't match what the
    // operator sees and feels wrong.
    const visibleRestrictions = getVisibleRestrictions(summary);
    const severityCounts = countBySeverity(visibleRestrictions);
    const totalAdvisories = visibleRestrictions.length;
    const advisoryLabel = `${totalAdvisories} airspace ${totalAdvisories === 1 ? 'advisory' : 'advisories'}`;

    // Weather lives inside the airport card whose METAR it came from. Only fall back
    // to a standalone strip when no visible card matches (rural pins, ASOS in a
    // facility we're not surfacing). While a new summary is in flight, hide weather
    // entirely — the weather we have is for the NEW click, the cards are for the OLD
    // click, and the station almost never matches across the two. Rendering during
    // that window flashes the strip above cards until the new summary lands.
    const displayWeather = isLoading ? null : weather;
    const weatherMatchesCard = findWeatherMatchKey(visibleRestrictions, displayWeather) !== null;

    const handleHeaderClose = () => {
        dismiss();
        onClose?.();
    };

    return (
        <VerticalContainer
            className={`${styles.sheet} ${stateClass} ${className}`}
            ref={sheetRef}
            testId={testId}>
            <SheetHeader
                address={address}
                dragHandleRef={dragHandleRef}
                onClose={handleHeaderClose}
                point={point}
                region={region}
            />

            <Wrapper className={styles.ctaSlot}>
                <NewOperationButton />
            </Wrapper>

            <HorizontalContainer className={styles.summaryRow}>
                <Wrapper className={styles.summaryLabel}>{advisoryLabel}</Wrapper>

                <AdvisoryCountChips counts={severityCounts} />
            </HorizontalContainer>

            {weatherMatchesCard ? null : <WeatherStrip weather={displayWeather} />}

            <VerticalContainer className={styles.cardsScroll}>
                <AirspaceCardList summary={summary} weather={displayWeather} />

                <FooterNote />
            </VerticalContainer>
        </VerticalContainer>
    );
};

export default AirspaceSheet;
